import React, { useState } from "react";
import { ActiveClient } from "../../types/contentTools";

interface CopyFromBlueprintPanelProps {
  clients: ActiveClient[];
  currentBlueprintId: string;
  onCopy: (sourceBlueprintId: string, copyCustomContentRows: boolean) => Promise<void>;
  onCopied: () => void;
}

/**
 * Copy the content artifacts (content row, link dump, default email template)
 * — and optionally the custom content row templates — from another client's
 * blueprint into the currently selected blueprint.
 */
const CopyFromBlueprintPanel: React.FC<CopyFromBlueprintPanelProps> = ({
  clients,
  currentBlueprintId,
  onCopy,
  onCopied,
}) => {
  const [sourceId, setSourceId] = useState<string>("");
  const [copyCustom, setCopyCustom] = useState<boolean>(true);
  const [pending, setPending] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(
    null,
  );

  const targets = clients.filter((c) => c.blueprint_id !== currentBlueprintId);

  const handleCopy = async () => {
    if (!sourceId) {
      setMessage({ type: "error", text: "Select a client to copy from." });
      return;
    }
    const confirmed = window.confirm(
      "This overwrites this blueprint's content row, link dump, and default email template. Continue?",
    );
    if (!confirmed) return;

    setPending(true);
    setMessage(null);
    try {
      await onCopy(sourceId, copyCustom);
      setMessage({ type: "success", text: "Copied." });
      onCopied();
    } catch (e: any) {
      setMessage({ type: "error", text: e?.message || "Failed to copy." });
    } finally {
      setPending(false);
    }
  };

  if (targets.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: "6px",
        padding: "16px",
        marginBottom: "16px",
        background: "#f7fafc",
      }}
    >
      <div style={{ fontWeight: 600, fontSize: "14px", color: "#2D3748", marginBottom: "8px" }}>
        Copy from another blueprint
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
        <select
          value={sourceId}
          onChange={(e) => setSourceId(e.target.value)}
          disabled={pending}
          style={{
            padding: "6px 10px",
            border: "1px solid #e2e8f0",
            borderRadius: "4px",
            fontSize: "14px",
            minWidth: "280px",
          }}
        >
          <option value="">Select a client…</option>
          {targets.map((c) => (
            <option key={c.blueprint_id} value={c.blueprint_id}>
              {c.title} — {c.customer_email ?? ""}
            </option>
          ))}
        </select>

        <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#4A5568" }}>
          <input
            type="checkbox"
            checked={copyCustom}
            onChange={(e) => setCopyCustom(e.target.checked)}
            disabled={pending}
          />
          Also copy custom content row templates
        </label>

        <button
          type="button"
          className="primary-button"
          onClick={handleCopy}
          disabled={pending || !sourceId}
        >
          {pending ? "Copying…" : "Copy into this blueprint"}
        </button>

        {message && (
          <span
            style={{
              fontSize: "13px",
              color: message.type === "success" ? "#2F855A" : "#C53030",
            }}
          >
            {message.text}
          </span>
        )}
      </div>
    </div>
  );
};

export default CopyFromBlueprintPanel;
